import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { coursesAPI } from '../services/api';
import { 
  BookOpen, Clock, Users, Star, Play, CheckCircle, Lock, 
  Download, ArrowLeft, User, Calendar, Award, FileText, 
  ExternalLink, MessageSquare, ThumbsUp, Eye, Pin, Edit, Trash2
} from 'lucide-react';

interface Lesson {
  title: string;
  description?: string;
  content: string;
  videoUrl?: string;
  subtitleUrl?: string;
  subtitleLanguage?: string;
  duration: number;
  order: number;
  resources?: Array<{ title: string; url: string; type: string }>;
  quiz?: {
    questions: Array<{
      question: string;
      options: string[];
      correctAnswer: number;
      explanation?: string;
    }>;
  };
}

interface Course {
  _id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail?: string;
  image?: string;
  video?: string;
  subtitleUrl?: string;
  subtitleLanguage?: string;
  instructor: {
    _id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    photo?: string;
    bio?: string;
  };
  level: string;
  duration: number;
  enrolledCount: number;
  rating: number;
  reviews?: Array<{
    user: { name?: string; firstName?: string; lastName?: string };
    rating: number;
    comment: string;
    createdAt: string;
  }>;
  isFree: boolean;
  price: number;
  category?: string;
  tags?: string[];
  lessons: Lesson[];
  certificateAvailable: boolean;
  whatStudentsWillLearn?: string[];
  skillsTheyWillGain?: string[];
  outcomesAfterCompletion?: string[];
  prerequisites?: Array<{ _id: string; title: string; thumbnail?: string }>;
  downloadablePDFs?: Array<{ title: string; url: string; description?: string; fileSize?: number }>;
  slides?: Array<{ title: string; url: string; description?: string; slideNumber?: number }>;
  externalLinks?: Array<{ title: string; url: string; description?: string; type: string }>;
}

interface Enrollment {
  _id: string;
  progress: number;
  currentLesson: number;
  completedLessons: number[];
  certificateIssued: boolean;
  certificateUrl?: string;
}

// Helper function to detect and convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  
  // Check if it's a YouTube URL
  // Match various YouTube URL formats:
  // - youtube.com/watch?v=VIDEO_ID
  // - youtube.com/embed/VIDEO_ID
  // - youtu.be/VIDEO_ID
  // - youtube.com/v/VIDEO_ID
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(youtubeRegex);
  
  if (match && match[1]) {
    const videoId = match[1];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  
  return null;
};

// Helper function to check if URL is YouTube
const isYouTubeUrl = (url: string): boolean => {
  if (!url) return false;
  // Check for youtube.com or youtu.be domains
  return /(?:youtube\.com|youtu\.be)/.test(url);
};

const t = (key: string, fallback: string) => fallback;

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'resources' | 'forum'>('overview');
  const [forumPosts, setForumPosts] = useState<any[]>([]);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [quizAnswers, setQuizAnswers] = useState<{ [lessonIndex: number]: number[] }>({});
  const [quizSubmitted, setQuizSubmitted] = useState<{ [lessonIndex: number]: boolean }>({});
  const [quizResults, setQuizResults] = useState<{ [lessonIndex: number]: any }>({});

  useEffect(() => {
    if (id) {
      loadCourse();
      loadForumPosts();
    }
  }, [id]);

  const loadForumPosts = async () => {
    if (!id) return;
    try {
      const response = await coursesAPI.getForumPosts(id);
      setForumPosts(response.data.data.posts || []);
    } catch (error: any) {
      console.error('Error loading forum posts:', error);
    }
  };

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await coursesAPI.getCourse(id!);
      const courseData = response.data.data.course;
      
      // Debug: Log subtitle information
      console.log('Course loaded:', {
        title: courseData.title,
        video: courseData.video,
        subtitleUrl: courseData.subtitleUrl,
        subtitleLanguage: courseData.subtitleLanguage,
        lessons: courseData.lessons?.map((l: any) => ({
          title: l.title,
          videoUrl: l.videoUrl,
          subtitleUrl: l.subtitleUrl,
          subtitleLanguage: l.subtitleLanguage
        }))
      });
      
      setCourse(courseData);
      setEnrollment(response.data.data.enrollment);
      if (response.data.data.enrollment) {
        setCurrentLessonIndex(response.data.data.enrollment.currentLesson);
      }
    } catch (error: any) {
      console.error('Error loading course:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setEnrolling(true);
      await coursesAPI.enrollInCourse(id!);
      await loadCourse(); // Recharger pour obtenir l'enrollment
    } catch (error: any) {
      console.error('Error enrolling:', error);
      alert(error.response?.data?.message || 'Error enrolling in course');
    } finally {
      setEnrolling(false);
    }
  };

  const handleLessonClick = (index: number) => {
    if (enrollment) {
      setCurrentLessonIndex(index);
    }
  };

  const getInstructorName = (instructor: Course['instructor']) => {
    if (instructor.firstName && instructor.lastName) {
      return `${instructor.firstName} ${instructor.lastName}`;
    }
    return instructor.name || 'Instructor';
  };

  const getLevelLabel = (level: string) => {
    const labels: { [key: string]: string } = {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced'
    };
    return labels[level] || level;
  };

  const isLessonCompleted = (index: number) => {
    return enrollment?.completedLessons.includes(index) || false;
  };

  const isLessonUnlocked = (index: number) => {
    if (!enrollment) return false;
    if (index === 0) return true;
    return isLessonCompleted(index - 1);
  };

  // Check if current user is the course creator
  const isCreator = user && course && course.instructor._id === user._id;

  // Handle delete course
  const handleDeleteCourse = async () => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      await coursesAPI.deleteCourse(id!);
      navigate('/courses');
    } catch (error: any) {
      console.error('Error deleting course:', error);
      alert(error.response?.data?.message || 'Failed to delete course');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course not found</h2>
          <Link to="/courses" className="text-green-600 hover:text-green-700">
            Back to courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/courses')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to courses
        </button>
        
        {/* Edit and Delete buttons for course creator */}
        {isCreator && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/courses/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Edit className="h-4 w-4" />
              Edit Course
            </button>
            <button
              onClick={handleDeleteCourse}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Course
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contenu principal */}
        <div className="lg:col-span-2">
          {/* Course Title and Video - Display at the top */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h1 className="text-3xl font-bold mb-4">{course.title}</h1>
            {course.video && (
              <div className="relative mb-6">
                {(() => {
                  const isYouTube = isYouTubeUrl(course.video);
                  const embedUrl = isYouTube ? getYouTubeEmbedUrl(course.video) : null;
                  
                  console.log('Video URL:', course.video);
                  console.log('Is YouTube:', isYouTube);
                  console.log('Embed URL:', embedUrl);
                  console.log('Course subtitleUrl:', course.subtitleUrl);
                  
                  if (isYouTube && embedUrl) {
                    return (
                      <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
                        <iframe
                          src={embedUrl}
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="Course video"
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div>
                        <video
                          controls
                          crossOrigin="anonymous"
                          className="w-full rounded-lg"
                          src={course.video}
                          onLoadedMetadata={(e) => {
                            const video = e.currentTarget;
                            console.log('Video metadata loaded');
                            console.log('Video src:', video.src);
                            console.log('Text tracks available:', video.textTracks.length);
                            
                            // Wait a bit for tracks to load
                            setTimeout(() => {
                              // Enable subtitle tracks
                              for (let i = 0; i < video.textTracks.length; i++) {
                                const track = video.textTracks[i];
                              console.log(`Track ${i}:`, {
                                kind: track.kind,
                                label: track.label,
                                language: track.language,
                                mode: track.mode,
                                cues: track.cues ? track.cues.length : 0
                              });
                                
                                if (track.kind === 'subtitles' || track.kind === 'captions') {
                                  // Set track to showing mode
                                  track.mode = 'showing';
                                  console.log('✅ Subtitle track enabled:', track.label, 'mode:', track.mode);
                                  
                                  // Force update if track has cues
                                  if (track.cues && track.cues.length > 0) {
                                    console.log(`Track has ${track.cues.length} cues`);
                                  }
                                }
                                
                                // Listen for track changes
                                track.addEventListener('change', () => {
                                  console.log('Track changed:', track.label, 'mode:', track.mode, 'cues:', track.cues ? track.cues.length : 0);
                                });
                                
                                // Listen for cue changes
                                track.addEventListener('cuechange', () => {
                                  if (track.activeCues && track.activeCues.length > 0) {
                                    const cue = track.activeCues[0];
                                    const cueText = cue instanceof VTTCue ? cue.text : 'Cue active';
                                    console.log('Active cue:', cueText);
                                  }
                                });
                              }
                            }, 1000);
                          }}
                          onError={(e) => {
                            console.error('Video error:', e);
                            const video = e.currentTarget;
                            if (video.error) {
                              console.error('Video error code:', video.error.code);
                              console.error('Video error message:', video.error.message);
                            }
                          }}
                        >
                          {course.subtitleUrl ? (() => {
                            // Ensure subtitle URL is properly formatted
                            let subtitleSrc = course.subtitleUrl;
                            
                            // Remove leading slash if present, then add it back to ensure consistency
                            subtitleSrc = subtitleSrc.replace(/^\/+/, '');
                            subtitleSrc = '/' + subtitleSrc;
                            
                            // In development, use proxy (setupProxy.js handles /uploads)
                            // In production, the backend serves /uploads directly
                            console.log('Subtitle URL:', subtitleSrc);
                            console.log('Subtitle Language:', course.subtitleLanguage);
                            
                            // Test if subtitle file is accessible
                            fetch(subtitleSrc, { method: 'HEAD' })
                              .then(response => {
                                console.log('Subtitle file accessible:', response.ok, 'Status:', response.status, 'Content-Type:', response.headers.get('Content-Type'));
                                if (!response.ok) {
                                  console.warn('Subtitle file returned status:', response.status);
                                }
                              })
                              .catch(error => {
                                console.error('Subtitle file not accessible:', error);
                              });
                            
                            return (
                              <track
                                key={`subtitle-${subtitleSrc}`}
                                kind="subtitles"
                                src={subtitleSrc}
                                srcLang={course.subtitleLanguage || 'fr'}
                                label={course.subtitleLanguage === 'fr' ? 'Français' : course.subtitleLanguage === 'en' ? 'English' : course.subtitleLanguage === 'ar' ? 'العربية' : course.subtitleLanguage || 'Subtitles'}
                                default
                                onLoad={() => {
                                  console.log('Subtitle track loaded successfully');
                                }}
                                onError={(e) => {
                                  console.error('Subtitle track error:', e);
                                }}
                              />
                            );
                          })() : (
                            <track kind="captions" src="" srcLang="fr" label="No subtitles" />
                          )}
                          Your browser does not support the video tag.
                        </video>
                        {!course.subtitleUrl && (
                          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                              <strong>💡 Note:</strong> {t('course.noSubtitleFile', 'No subtitle file is associated with this video.')} 
                              Subtitles are generated automatically when the video is uploaded. If subtitles are not available, verify that the video was uploaded successfully.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }
                })()}
                {!user && (
                  <div
                    onClick={() => navigate('/login')}
                    className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 rounded-lg cursor-pointer z-10 hover:bg-black/75 transition-colors"
                  >
                    <Lock className="h-16 w-16 text-white mb-3" />
                    <p className="text-xl font-semibold text-white">Sign in to watch</p>
                    <button className="mt-3 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
                      Sign in
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Lesson Content - Show if enrolled or if user wants to preview */}
          {(enrollment && course.lessons[currentLessonIndex]) || (!enrollment && course.lessons[currentLessonIndex]) ? (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-2xl font-bold mb-4">
                {course.lessons[currentLessonIndex].title}
              </h2>
              {course.lessons[currentLessonIndex].videoUrl && (
                <div className="relative mb-6">
                  {(() => {
                    const videoUrl = course.lessons[currentLessonIndex].videoUrl!;
                    const isYouTube = isYouTubeUrl(videoUrl);
                    const embedUrl = isYouTube ? getYouTubeEmbedUrl(videoUrl) : null;
                    
                    console.log('Lesson Video URL:', videoUrl);
                    console.log('Is YouTube:', isYouTube);
                    console.log('Embed URL:', embedUrl);
                    
                    if (isYouTube && embedUrl) {
                      return (
                        <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
                          <iframe
                            src={embedUrl}
                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={course.lessons[currentLessonIndex].title}
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div>
                          <video
                            controls
                            crossOrigin="anonymous"
                            className="w-full rounded-lg"
                            src={videoUrl}
                            onLoadedMetadata={(e) => {
                              const video = e.currentTarget;
                              console.log('Lesson video metadata loaded');
                              console.log('Text tracks available:', video.textTracks.length);
                              
                              // Enable subtitle tracks
                              for (let i = 0; i < video.textTracks.length; i++) {
                                const track = video.textTracks[i];
                                console.log(`Lesson Track ${i}:`, {
                                  kind: track.kind,
                                  label: track.label,
                                  language: track.language,
                                  mode: track.mode,
                                  cues: track.cues ? track.cues.length : 0
                                });
                                if (track.kind === 'subtitles' || track.kind === 'captions') {
                                  track.mode = 'showing';
                                  console.log('Lesson subtitle track enabled:', track.label);
                                }
                                
                                // Listen for cue changes
                                track.addEventListener('cuechange', () => {
                                  if (track.activeCues && track.activeCues.length > 0) {
                                    console.log('Lesson active cues:', track.activeCues.length);
                                  }
                                });
                                
                                // Listen for track changes
                                track.addEventListener('change', () => {
                                  console.log('Lesson track changed:', track.label, 'mode:', track.mode, 'cues:', track.cues ? track.cues.length : 0);
                                });
                              }
                            }}
                            onError={(e) => {
                              console.error('Lesson video error:', e);
                              const video = e.currentTarget;
                              if (video.error) {
                                console.error('Lesson video error code:', video.error.code);
                                console.error('Lesson video error message:', video.error.message);
                              }
                            }}
                          >
                            {course.lessons[currentLessonIndex].subtitleUrl && (() => {
                              // Ensure subtitle URL is properly formatted
                              let subtitleSrc = course.lessons[currentLessonIndex].subtitleUrl!;
                              
                              // Remove leading slash if present, then add it back to ensure consistency
                              subtitleSrc = subtitleSrc.replace(/^\/+/, '');
                              subtitleSrc = '/' + subtitleSrc;
                              
                              // In development, use proxy (setupProxy.js handles /uploads)
                              // In production, the backend serves /uploads directly
                              console.log('Lesson Subtitle URL:', subtitleSrc);
                              console.log('Lesson Subtitle Language:', course.lessons[currentLessonIndex].subtitleLanguage);
                              
                              // Test if subtitle file is accessible
                              fetch(subtitleSrc, { method: 'HEAD' })
                                .then(response => {
                                  console.log('Lesson subtitle file accessible:', response.ok, 'Status:', response.status, 'Content-Type:', response.headers.get('Content-Type'));
                                  if (!response.ok) {
                                    console.warn('Lesson subtitle file returned status:', response.status);
                                  }
                                })
                                .catch(error => {
                                  console.error('Lesson subtitle file not accessible:', error);
                                });
                              
                              return (
                                <track
                                  key={`lesson-subtitle-${subtitleSrc}`}
                                  kind="subtitles"
                                  src={subtitleSrc}
                                  srcLang={course.lessons[currentLessonIndex].subtitleLanguage || 'fr'}
                                  label={course.lessons[currentLessonIndex].subtitleLanguage === 'fr' ? 'Français' : course.lessons[currentLessonIndex].subtitleLanguage === 'en' ? 'English' : course.lessons[currentLessonIndex].subtitleLanguage === 'ar' ? 'العربية' : course.lessons[currentLessonIndex].subtitleLanguage || 'Subtitles'}
                                  default
                                  onLoad={() => {
                                    console.log('Lesson subtitle track loaded successfully');
                                  }}
                                  onError={(e) => {
                                    console.error('Lesson subtitle track error:', e);
                                  }}
                                />
                              );
                            })()}
                          </video>
                          {!course.lessons[currentLessonIndex].subtitleUrl && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">
                                <strong>💡 Note:</strong> No subtitle file is associated with this lesson. 
                                Subtitles are generated automatically when the lesson video is uploaded. If subtitles are not available, verify that the video was uploaded successfully.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    }
                  })()}
                  {!user && (
                    <div
                      onClick={() => navigate('/login')}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 rounded-lg cursor-pointer z-10 hover:bg-black/75 transition-colors"
                    >
                      <Lock className="h-16 w-16 text-white mb-3" />
                      <p className="text-xl font-semibold text-white">Sign in to watch</p>
                      <button className="mt-3 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
                        Sign in
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: course.lessons[currentLessonIndex].content }}
              />
              {course.lessons[currentLessonIndex].resources && course.lessons[currentLessonIndex].resources!.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-lg font-semibold mb-4">Resources</h3>
                  <div className="space-y-2">
                    {course.lessons[currentLessonIndex].resources!.map((resource, idx) => (
                      <a
                        key={idx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-green-600 hover:text-green-700"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {resource.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Quiz Section - Only for enrolled users */}
              {enrollment && course.lessons[currentLessonIndex].quiz && course.lessons[currentLessonIndex].quiz!.questions.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold mb-4">Quiz</h3>
                  {quizSubmitted[currentLessonIndex] && quizResults[currentLessonIndex] ? (
                    <div className="p-4 bg-green-50 rounded-lg mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">Quiz Results</span>
                        <span className={`text-lg font-bold ${quizResults[currentLessonIndex].passed ? 'text-green-600' : 'text-red-600'}`}>
                          {quizResults[currentLessonIndex].score}/{quizResults[currentLessonIndex].maxScore} ({quizResults[currentLessonIndex].percentage}%)
                        </span>
                      </div>
                      <p className={`text-sm ${quizResults[currentLessonIndex].passed ? 'text-green-700' : 'text-red-700'}`}>
                        {quizResults[currentLessonIndex].passed ? '✓ You passed!' : '✗ You need 70% to pass. Try again!'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {course.lessons[currentLessonIndex].quiz!.questions.map((question, qIndex) => (
                        <div key={qIndex} className="p-4 border border-gray-200 rounded-lg">
                          <h4 className="font-semibold mb-3">
                            Question {qIndex + 1}: {question.question}
                          </h4>
                          <div className="space-y-2">
                            {question.options.map((option, oIndex) => (
                              <label
                                key={oIndex}
                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                  quizAnswers[currentLessonIndex]?.[qIndex] === oIndex
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200 hover:border-green-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`quiz-${currentLessonIndex}-${qIndex}`}
                                  value={oIndex}
                                  checked={quizAnswers[currentLessonIndex]?.[qIndex] === oIndex}
                                  onChange={() => {
                                    const newAnswers = { ...quizAnswers };
                                    if (!newAnswers[currentLessonIndex]) {
                                      newAnswers[currentLessonIndex] = [];
                                    }
                                    newAnswers[currentLessonIndex][qIndex] = oIndex;
                                    setQuizAnswers(newAnswers);
                                  }}
                                  className="mr-3"
                                />
                                <span className="dark:text-white">{option}</span>
                              </label>
                            ))}
                          </div>
                          {quizSubmitted[currentLessonIndex] && question.explanation && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                              <p className="text-sm text-blue-800">
                                <strong>Explanation:</strong> {question.explanation}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={async () => {
                          if (!quizAnswers[currentLessonIndex] || quizAnswers[currentLessonIndex].length !== course.lessons[currentLessonIndex].quiz!.questions.length) {
                            alert('Please answer all questions');
                            return;
                          }
                          try {
                            const response = await coursesAPI.submitQuiz(id!, currentLessonIndex, quizAnswers[currentLessonIndex]);
                            setQuizResults(prev => ({ ...prev, [currentLessonIndex]: response.data.data }));
                            setQuizSubmitted(prev => ({ ...prev, [currentLessonIndex]: true }));
                          } catch (error: any) {
                            console.error('Error submitting quiz:', error);
                            alert(error.response?.data?.message || 'Failed to submit quiz');
                          }
                        }}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Submit Quiz
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 flex gap-4">
                {currentLessonIndex > 0 && (
                  <button
                    onClick={() => setCurrentLessonIndex(currentLessonIndex - 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Previous Lesson
                  </button>
                )}
                {currentLessonIndex < course.lessons.length - 1 && (
                  <button
                    onClick={() => setCurrentLessonIndex(currentLessonIndex + 1)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Next Lesson
                  </button>
                )}
                {enrollment && currentLessonIndex === course.lessons.length - 1 && (
                  <button
                    onClick={async () => {
                      await coursesAPI.updateProgress(id!, currentLessonIndex);
                      await loadCourse();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Mark as Completed
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tabs */}
              <div className="bg-white rounded-lg shadow-sm">
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`px-6 py-4 text-sm font-medium border-b-2 ${
                        activeTab === 'overview'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('resources')}
                      className={`px-6 py-4 text-sm font-medium border-b-2 ${
                        activeTab === 'resources'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Resources
                    </button>
                    <button
                      onClick={() => setActiveTab('forum')}
                      className={`px-6 py-4 text-sm font-medium border-b-2 ${
                        activeTab === 'forum'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Forum
                    </button>
                  </nav>
                </div>

                <div className="p-6">
                  {activeTab === 'overview' && (
                    <div>
                      <h2 className="text-2xl font-bold mb-4">About this course</h2>
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: course.description }}
                      />
                      
                      {/* Course Objectives */}
                      {(course.whatStudentsWillLearn?.length || course.skillsTheyWillGain?.length) && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <h3 className="text-xl font-semibold mb-6">Course Objectives</h3>
                          
                          {course.whatStudentsWillLearn && course.whatStudentsWillLearn.length > 0 && (
                            <div className="mb-6">
                              <h4 className="text-lg font-medium mb-3">What students will learn</h4>
                              <ul className="space-y-2">
                                {course.whatStudentsWillLearn.map((item, index) => (
                                  <li key={index} className="flex items-start gap-2">
                                    <span className="text-green-600 mt-1">•</span>
                                    <span className="text-gray-700">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {course.skillsTheyWillGain && course.skillsTheyWillGain.length > 0 && (
                            <div className="mb-6">
                              <h4 className="text-lg font-medium mb-3">Skills they will gain</h4>
                              <ul className="space-y-2">
                                {course.skillsTheyWillGain.map((item, index) => (
                                  <li key={index} className="flex items-start gap-2">
                                    <span className="text-green-600 mt-1">•</span>
                                    <span className="text-gray-700">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Rating & Reviews */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold mb-4">Rating & Reviews</h3>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="text-center">
                            <div className="text-4xl font-bold text-gray-900">{course.rating > 0 ? course.rating.toFixed(1) : 'N/A'}</div>
                            <div className="flex items-center justify-center gap-1 mt-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-5 w-5 ${
                                    star <= Math.round(course.rating)
                                      ? 'text-yellow-400 fill-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Users className="h-5 w-5 text-gray-500" />
                              <span className="text-sm text-gray-600">
                                {course.enrolledCount} {course.enrolledCount === 1 ? 'learner' : 'learners'}
                              </span>
                            </div>
                            {course.reviews && course.reviews.length > 0 && (
                              <p className="text-sm text-gray-600">
                                Based on {course.reviews.length} {course.reviews.length === 1 ? 'review' : 'reviews'}
                              </p>
                            )}
                          </div>
                        </div>
                        {course.reviews && course.reviews.length > 0 && (
                          <div className="space-y-4">
                            {course.reviews.slice(0, 3).map((review, idx) => (
                              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold">
                                    {review.user.firstName && review.user.lastName
                                      ? `${review.user.firstName} ${review.user.lastName}`
                                      : review.user.name || 'Anonymous'}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`h-4 w-4 ${
                                          star <= review.rating
                                            ? 'text-yellow-400 fill-yellow-400'
                                            : 'text-gray-300'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600">{review.comment}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'resources' && (
                    <div className="space-y-6">
                      {/* Downloadable PDFs */}
                      {course.downloadablePDFs && course.downloadablePDFs.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Downloadable PDFs
                          </h3>
                          <div className="space-y-2">
                            {course.downloadablePDFs.map((pdf, idx) => (
                              <a
                                key={idx}
                                href={pdf.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <FileText className="h-5 w-5 text-red-600" />
                                  <div>
                                    <p className="font-medium">{pdf.title}</p>
                                    {pdf.description && (
                                      <p className="text-sm text-gray-600">{pdf.description}</p>
                                    )}
                                    {pdf.fileSize && (
                                      <p className="text-xs text-gray-500">
                                        {(pdf.fileSize / (1024 * 1024)).toFixed(2)} MB
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <Download className="h-5 w-5 text-gray-400" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Slides */}
                      {course.slides && course.slides.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Slides
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {course.slides.map((slide, idx) => (
                              <a
                                key={idx}
                                href={slide.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3 mb-2">
                                  <FileText className="h-5 w-5 text-blue-600" />
                                  <span className="text-sm font-medium text-gray-500">
                                    Slide {slide.slideNumber || idx + 1}
                                  </span>
                                </div>
                                <p className="font-medium">{slide.title}</p>
                                {slide.description && (
                                  <p className="text-sm text-gray-600 mt-1">{slide.description}</p>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* External Links */}
                      {course.externalLinks && course.externalLinks.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <ExternalLink className="h-5 w-5" />
                            External Links & Resources
                          </h3>
                          <div className="space-y-2">
                            {course.externalLinks.map((link, idx) => (
                              <a
                                key={idx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <ExternalLink className="h-5 w-5 text-green-600" />
                                  <div>
                                    <p className="font-medium">{link.title}</p>
                                    {link.description && (
                                      <p className="text-sm text-gray-600">{link.description}</p>
                                    )}
                                    <span className="text-xs text-gray-500 capitalize">{link.type}</span>
                                  </div>
                                </div>
                                <ExternalLink className="h-4 w-4 text-gray-400" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'forum' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">Course Forum</h3>
                        {user && (
                          <button
                            onClick={async () => {
                              if (newPostTitle && newPostContent && id) {
                                try {
                                  await coursesAPI.createForumPost(id, {
                                    title: newPostTitle,
                                    content: newPostContent
                                  });
                                  setNewPostTitle('');
                                  setNewPostContent('');
                                  await loadForumPosts();
                                } catch (error: any) {
                                  console.error('Error creating post:', error);
                                }
                              }
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            New Post
                          </button>
                        )}
                      </div>
                      {user && (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <input
                            type="text"
                            value={newPostTitle}
                            onChange={(e) => setNewPostTitle(e.target.value)}
                            placeholder="Post title..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                          />
                          <textarea
                            value={newPostContent}
                            onChange={(e) => setNewPostContent(e.target.value)}
                            placeholder="Write your post..."
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                          />
                        </div>
                      )}
                      <div className="space-y-4">
                        {forumPosts.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">
                            No forum posts yet. Be the first to start a discussion!
                          </p>
                        ) : (
                          forumPosts.map((post) => (
                            <div key={post._id} className="p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {post.isPinned && <Pin className="h-4 w-4 text-yellow-500" />}
                                  <h4 className="font-semibold">{post.title}</h4>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm text-gray-500 flex items-center gap-1">
                                    <Eye className="h-4 w-4" />
                                    {post.views || 0}
                                  </span>
                                  <button
                                    onClick={async () => {
                                      if (id) {
                                        await coursesAPI.toggleLike(id, post._id);
                                        await loadForumPosts();
                                      }
                                    }}
                                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-green-600"
                                  >
                                    <ThumbsUp className={`h-4 w-4 ${post.likes?.some((like: any) => like.toString() === user?._id) ? 'fill-current text-green-600' : ''}`} />
                                    {post.likes?.length || 0}
                                  </button>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{post.content}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>{post.replies?.length || 0} replies</span>
                                <span className="text-xs">
                                  by {post.author?.firstName && post.author?.lastName
                                    ? `${post.author.firstName} ${post.author.lastName}`
                                    : post.author?.name || 'Anonymous'}
                                </span>
                              </div>
                              {post.replies && post.replies.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  {post.replies.slice(0, 2).map((reply: any, idx: number) => (
                                    <div key={idx} className="mb-2 text-sm">
                                      <span className="font-medium">
                                        {reply.author?.firstName && reply.author?.lastName
                                          ? `${reply.author.firstName} ${reply.author.lastName}`
                                          : reply.author?.name || 'Anonymous'}
                                      </span>
                                      <span className="text-gray-600">: {reply.content}</span>
                                    </div>
                                  ))}
                                  {post.replies.length > 2 && (
                                    <button className="text-sm text-green-600 hover:underline">
                                      View all {post.replies.length} replies
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lessons List */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold mb-4">Course Content</h2>
            <div className="space-y-2">
              {course.lessons.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  No lessons available yet.
                </p>
              ) : (
                course.lessons.map((lesson, index) => (
                  <div
                    key={index}
                    className={`p-4 border rounded-lg transition-colors ${
                      enrollment && isLessonUnlocked(index)
                        ? 'border-green-300 hover:bg-green-50 cursor-pointer'
                        : enrollment
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                        : 'border-gray-200 bg-gray-50'
                    } ${
                      currentLessonIndex === index ? 'bg-green-100 border-green-500' : ''
                    }`}
                    onClick={() => {
                      if (enrollment && isLessonUnlocked(index)) {
                        handleLessonClick(index);
                      } else if (!enrollment) {
                        // Show lesson preview for non-enrolled users
                        setCurrentLessonIndex(index);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {enrollment && isLessonCompleted(index) ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : enrollment && isLessonUnlocked(index) ? (
                          <Play className="h-5 w-5 text-green-600" />
                        ) : enrollment ? (
                          <Lock className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Play className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <h3 className="font-semibold">{lesson.title}</h3>
                          {lesson.description && (
                            <p className="text-sm text-gray-600">{lesson.description}</p>
                          )}
                          {lesson.quiz && lesson.quiz.questions.length > 0 && (
                            <span className="text-xs text-blue-600 mt-1 inline-block">
                              {lesson.quiz.questions.length} question{lesson.quiz.questions.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {lesson.duration} min
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Course Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {course.thumbnail && (
              <img
                src={course.thumbnail}
                alt={course.title}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            )}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-green-600">
                  {course.isFree ? 'Free' : `$${course.price}`}
                </span>
                {enrollment && (
                  <div className="text-sm text-gray-600">
                    Progress: {enrollment.progress}%
                  </div>
                )}
              </div>
              {!enrollment ? (
                <button
                  onClick={handleEnroll}
                  disabled={enrolling || !user}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {!user ? 'Sign in to Enroll' : enrolling ? 'Enrolling...' : 'Enroll in Course'}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${enrollment.progress}%` }}
                    />
                  </div>
                  {enrollment.certificateIssued && (
                    <a
                      href={enrollment.certificateUrl}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Award className="h-4 w-4" />
                      Download Certificate
                    </a>
                  )}
                  
                  {/* Student Outcomes - Only shown after course completion */}
                  {enrollment.progress === 100 && course.outcomesAfterCompletion && course.outcomesAfterCompletion.length > 0 && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <Award className="h-5 w-5 text-green-600" />
                        Congratulations! You've completed this course
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">Here's what you've achieved:</p>
                      <ul className="space-y-2">
                        {course.outcomesAfterCompletion.map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-600 mt-1">✓</span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-4">Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center text-gray-600">
                <Clock className="h-4 w-4 mr-2" />
                Duration: {course.duration} minutes
              </div>
              <div className="flex items-center text-gray-600">
                <Users className="h-4 w-4 mr-2" />
                {course.enrolledCount} students
              </div>
              <div className="flex items-center text-gray-600">
                <Star className="h-4 w-4 mr-2" />
                Rating: {course.rating > 0 ? course.rating.toFixed(1) : 'New'}
              </div>
              <div className="flex items-center text-gray-600">
                <BookOpen className="h-4 w-4 mr-2" />
                Level: {getLevelLabel(course.level)}
              </div>
            </div>
          </div>

          {/* Instructor */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-4">Instructor</h3>
            <div className="flex items-center gap-3">
              {course.instructor.photo ? (
                <img
                  src={course.instructor.photo}
                  alt={getInstructorName(course.instructor)}
                  className="h-12 w-12 rounded-full"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-600" />
                </div>
              )}
              <div>
                <div className="font-semibold">{getInstructorName(course.instructor)}</div>
                {course.instructor.bio && (
                  <p className="text-sm text-gray-600">{course.instructor.bio}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;

